import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Button,
  Box,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Info as InfoIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';

const SubscriptionExpiryModal = ({ open, userProfile }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();

  const handleUpgradeClick = () => {
    // Navigate to subscription tab in dashboard using search params
    setSearchParams({ tab: '3' });
    // Close modal by navigating to dashboard if not already there
    if (window.location.pathname !== '/dashboard') {
      navigate('/dashboard?tab=3');
    }
  };

  const isTrialExpired = () => {
    if (!userProfile?.subscription) return false;
    
    const now = new Date();
    const endDate = userProfile.subscription.endDate?.toDate ? 
      userProfile.subscription.endDate.toDate() : 
      new Date(userProfile.subscription.endDate);
    
    return userProfile.subscription.plan === 'trial' && endDate <= now;
  };

  const isSubscriptionExpired = () => {
    if (!userProfile?.subscriptionStatus) return false;
    return userProfile.subscriptionStatus === 'expired';
  };

  const getModalContent = () => {
    if (isTrialExpired()) {
      return {
        title: t('subscriptionModal.trialExpiredTitle'),
        message: t('subscriptionModal.trialExpiredSimpleMessage'),
        buttonText: t('subscriptionModal.chooseSubscription')
      };
    } else if (isSubscriptionExpired()) {
      return {
        title: t('subscriptionModal.subscriptionExpiredTitle'),
        message: t('subscriptionModal.subscriptionExpiredSimpleMessage'),
        buttonText: t('subscriptionModal.renewSubscription')
      };
    } else {
      return {
        title: t('subscriptionModal.accessRestrictedTitle'),
        message: t('subscriptionModal.accessRestrictedSimpleMessage'),
        buttonText: t('subscriptionModal.updateSubscription')
      };
    }
  };

  const content = getModalContent();

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: { xs: 0, sm: 3 },
          m: { xs: 0, sm: 2 },
          maxHeight: { xs: '100vh', sm: 'calc(100vh - 64px)' },
          width: { xs: '100vw', sm: 'auto' }
        }
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            color: '#334155',
            p: { xs: 3, sm: 4 },
            textAlign: 'center',
            position: 'relative',
            borderBottom: '1px solid #e2e8f0'
          }}
        >
          <Box
            sx={{
              backgroundColor: '#3b82f6',
              borderRadius: '50%',
              width: { xs: 48, sm: 60 },
              height: { xs: 48, sm: 60 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2
            }}
          >
            <InfoIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: 'white' }} />
          </Box>
          
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: 1,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              color: '#1e293b'
            }}
          >
            {content.title}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Typography
          variant="body1"
          sx={{
            textAlign: 'center',
            mb: 4,
            fontSize: { xs: '1rem', sm: '1.1rem' },
            lineHeight: 1.6,
            color: '#64748b'
          }}
        >
          {content.message}
        </Typography>

        {/* Action Buttons */}
        <Box textAlign="center">
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleUpgradeClick}
            endIcon={<ArrowForwardIcon />}
            sx={{
              py: { xs: 1.5, sm: 2 },
              backgroundColor: '#3b82f6',
              fontSize: { xs: '1rem', sm: '1.1rem' },
              fontWeight: 600,
              borderRadius: 2,
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
              mb: 2,
              '&:hover': {
                backgroundColor: '#2563eb',
                boxShadow: '0 6px 16px rgba(59, 130, 246, 0.4)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            {content.buttonText}
          </Button>
          
          <Button
            variant="outlined"
            size="large"
            fullWidth
            onClick={() => window.location.href = 'mailto:support@omnivers.fr?subject=Support Abonnement - BookingBarber'}
            sx={{
              py: { xs: 1.5, sm: 2 },
              borderColor: '#64748b',
              color: '#64748b',
              fontSize: { xs: '0.9rem', sm: '1rem' },
              fontWeight: 500,
              borderRadius: 2,
              textTransform: 'none',
              '&:hover': {
                borderColor: '#475569',
                backgroundColor: '#f8fafc'
              },
              transition: 'all 0.2s ease'
            }}
          >
            {t('subscriptionModal.contactSupport')}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionExpiryModal;